import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff } from "react-icons/fi";
import api from "../utils/api";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";

export default function BlogList() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBlogs = () => {
    setLoading(true);
    api
      .get("/blogs/admin/all")
      .then((res) => setBlogs(res.data))
      .catch(() => toast.error("Failed to load blog posts"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete blog post "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/blogs/${id}`);
      toast.success("Blog post deleted");
      setBlogs(blogs.filter((b) => b._id !== id));
    } catch (err) {
      toast.error("Failed to delete blog post");
    }
  };

  const togglePublish = async (blog) => {
    try {
      const { data } = await api.put(`/blogs/${blog._id}`, { published: !blog.published });
      setBlogs(blogs.map((b) => (b._id === blog._id ? data : b)));
      toast.success(data.published ? "Published" : "Unpublished");
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl text-maroon-800">Blog Posts</h1>
        <Link to="/admin/blogs/new" className="inline-flex items-center gap-2 bg-saffron-500 hover:bg-saffron-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors">
          <FiPlus /> New Post
        </Link>
      </div>

      {blogs.length === 0 ? (
        <EmptyState title="No blog posts yet" subtitle="Click 'New Post' to write your first article." />
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-5 py-3 font-medium">Title</th>
                <th className="px-5 py-3 font-medium">Category</th>
                <th className="px-5 py-3 font-medium">Author</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Views</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.map((b) => (
                <tr key={b._id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-maroon-800 max-w-xs truncate">{b.title}</td>
                  <td className="px-5 py-3 text-gray-600">{b.category}</td>
                  <td className="px-5 py-3 text-gray-600">{b.author}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${b.published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {b.published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-600">{b.views}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => togglePublish(b)} title={b.published ? "Unpublish" : "Publish"} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">
                        {b.published ? <FiEyeOff /> : <FiEye />}
                      </button>
                      <Link to={`/admin/blogs/edit/${b._id}`} className="p-2 rounded-lg hover:bg-gray-100 text-saffron-600">
                        <FiEdit2 />
                      </Link>
                      <button onClick={() => handleDelete(b._id, b.title)} className="p-2 rounded-lg hover:bg-gray-100 text-red-500">
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
